/* eslint-disable react/no-unescaped-entities */
/* eslint-disable react/jsx-no-constructed-context-values */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, createContext } from "react";
import {
  PublicClientApplication,
  EventType,
  InteractionStatus,
} from "@azure/msal-browser";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { Button, Form, Input } from "antd";
import { msalConfig, loginRequest } from "./config";
import LoginSkeleton from "./loader.jsx";
import tokenManager from "../core/TokenManager";
import { metrics } from "../metrics/client";
import { safeStorage } from "../utils/safeStorage";

export const UserContext = React.createContext();

export function handleLogin(instance) {
  instance.loginRedirect(loginRequest).catch((e) => {
    console.error("Login Redirect Error: ", e);
  });
}

export function handleLogout(instance) {
  instance.logoutRedirect().catch((e) => {
    console.error("Logout Redirect Error: ", e);
  });
}

export const SignOutButton = () => {
  const { instance } = useMsal();
  return (
    <Button
      type="link"
      //   icon={<LogOut height={20} width={20} />}
      onClick={() => handleLogout(instance)}
    >
      Logout
    </Button>
  );
};
const ComponentForAuthentication = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      handleLogin(instance);
    }
  }, [isAuthenticated, inProgress, instance]);

  const requestProfileData = React.useCallback(() => {
    const accounts = instance.getAllAccounts();
    const account = instance.getActiveAccount() || accounts[0];
    if (!account) {
      console.log("[MSAL] no account, skip acquireTokenSilent");
      return;
    }
    const request = { ...loginRequest, account };
 
    instance
      .acquireTokenSilent(request)
      .then((response) => {
        safeStorage.setItem(
          "currUrl",
          instance?.config?.auth?.state || window.location.pathname
        );
      })
      .catch(async (e) => {
        // Telemetry: silent token acquisition failed
        metrics.add(metrics.authErrorEvent({
          error_type: 'acquireTokenSilent_failed',
          silent_or_interactive: 'silent',
          error_message: e?.message || String(e),
        }));
        try {
          const pop = await instance.acquireTokenPopup(request);
        } catch (err) {
          console.error("[MSAL] popup failed:", err);
          // Telemetry: interactive (popup) token acquisition also failed
          metrics.add(metrics.authErrorEvent({
            error_type: 'acquireTokenPopup_failed',
            silent_or_interactive: 'interactive',
            error_message: err?.message || String(err),
          }));
        }
      });
  }, [instance, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && !safeStorage.getItem("id_token")) {
      requestProfileData();
    }
  }, [isAuthenticated]);
  const activeAccount = instance.getActiveAccount();

  console.log("auth useEffect", {
    isAuthenticated,
    activeAccount,
  });

  if (isAuthenticated) {
    return <>{children}</>;
  } else return <LoginSkeleton />;

  return (
    <div className="h-screen flex justify-center items-center bg-[#f5f5f7]">
      <div className="min-w-[420px] px-6 py-10 bg-white rounded-[10px] shadow-[0_4px_12px_rgba(0,0,0,0.1)]">
        <div className="text-center mb-4">
          <h2 className="text-accent text-[28px]/[32px] font-bold mb-8">
            Alspire 360
          </h2>
          <h3 className="text-[22px]/[28px] font-semibold text-text mb-6">
            Sign In
          </h3>
          <p className="text-left text-lightText">
            Sign in with your email address
          </p>
        </div>

        <Form layout="vertical" onFinish={handleLogin} className="gap-y-4">
          <Form.Item
            name="email"
            rules={[{ required: true, message: "Please input your email!" }]}
            className="mb-4"
          >
            <Input
              placeholder="Enter address"
              size="large"
              className="text-[14px]/[20px] py-[10px] px-3"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: "Please input your password!" }]}
            className="mb-4"
          >
            <Input.Password
              placeholder="Password"
              size="large"
              className="text-[14px]/[20px] py-[10px] px-3"
            />
          </Form.Item>

          <div className="mb-6">
            <a href="#" className="text-accent text-[12px]/[18px]">
              Forgot your password?
            </a>
          </div>

          <Form.Item className="mb-4">
            <Button type="primary" htmlType="submit" size="large" block>
              Sign In
            </Button>
          </Form.Item>
          <div className="text-center">
            <a href="#" className="text-accent text-[14px]/[22px] underline">
              Sign in through organization SSO
            </a>
          </div>
        </Form>
      </div>
    </div>
  );
};

// Simple mock provider for bypassed auth
const MockMsalProvider = ({ children }) => {
  return <>{children}</>;
};

const isAuthBypassed = process.env.REACT_APP_BYPASS_AUTH === 'true';

export const withAuth = (AppComponent) =>
  function AuthWrapped(props) {
    const [msalInstance, setMsalInstance] = useState(null);
    const [isInitialized, setIsInitialized] = useState(isAuthBypassed);
    const [initError, setInitError] = useState(null);

    // MSAL initialization — skipped entirely when auth is bypassed
    useEffect(() => {
      if (isAuthBypassed) {
        console.log('Authentication bypassed for development environment');
        return;
      }

      const initializeMsal = async () => {
        try {
          let newConfig = JSON.parse(JSON.stringify(msalConfig));
          newConfig.auth.state = window.location.pathname;

          const instance = new PublicClientApplication(newConfig);
          await instance.initialize();

          if (!instance.initializeWrapperLibrary) {
            instance.initializeWrapperLibrary = () => Promise.resolve();
          }

          if (
            !instance.getActiveAccount() &&
            instance.getAllAccounts().length > 0
          ) {
            instance.setActiveAccount(instance.getAllAccounts()[0]);
          }

          instance.addEventCallback((event) => {
            if (
              (event.eventType === EventType.LOGIN_SUCCESS ||
                event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
                event.eventType === EventType.SSO_SILENT_SUCCESS) &&
              event.payload?.account
            ) {
              instance.setActiveAccount(event.payload.account);
              // Keep TokenManager's account reference in sync with MSAL
              tokenManager.setAccount(event.payload.account);
            }
          });

          // Early-initialize TokenManager so the Axios interceptor in
          // index-core.js can attach tokens even before React renders.
          // App.js will call initialize() again (idempotent) once the
          // useMsal hook provides the account — this is a belt-and-suspenders
          // approach to eliminate the race condition (RCA 4B).
          const earlyAccount = instance.getActiveAccount() || instance.getAllAccounts()[0];
          if (earlyAccount) {
            tokenManager.initialize(instance, earlyAccount);
          }

          setMsalInstance(instance);
          setIsInitialized(true);
        } catch (error) {
          console.error("Failed to initialize MSAL:", error);
          // Telemetry: MSAL initialization failure
          metrics.add(metrics.authErrorEvent({
            error_type: 'msal_init_failed',
            silent_or_interactive: 'silent',
            error_message: error?.message || String(error),
          }));
          setInitError(error);
          setIsInitialized(true);
        }
      };

      initializeMsal();
    }, []);

    // Bypass — render without MsalProvider
    if (isAuthBypassed) {
      return (
        <MockMsalProvider>
          <AppComponent {...props} />
        </MockMsalProvider>
      );
    }

    if (!isInitialized) {
      return <LoginSkeleton />;
    }

    if (initError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="bg-white rounded-lg shadow-lg border border-red-200 p-8 max-w-md w-full text-center">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-500 text-2xl font-bold">!</span>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Authentication Error
            </h2>
            <p className="text-gray-600 mb-6">
              Failed to initialize authentication. Please check your configuration or set REACT_APP_BYPASS_AUTH=true for development.
            </p>
            <p className="text-sm text-gray-500">
              Error: {initError.message}
            </p>
          </div>
        </div>
      );
    }

    if (!msalInstance) {
      console.error("No MSAL instance available");
      return null;
    }

    return (
      <MsalProvider instance={msalInstance}>
        <UserContext.Provider value={{}}>
          <ComponentForAuthentication>
            <AppComponent {...props} />
          </ComponentForAuthentication>
        </UserContext.Provider>
      </MsalProvider>
    );
  };
